@if (
  isLoading.document ||
  isLoading.documentSummary ||
  isLoading.page ||
  isLoading.save ||
  isLoading.editSentiment ||
  isLoading.editEntity ||
  isLoading.editTopic
) {
  <div class="loading">
    <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
  </div>
}

<ng-container *transloco="let t; read: 'documents.view'">
  @if (!isLoading.document) {
    <div class="document-view__wrapper">
      <div class="document-wrapper">
        <div class="content-wrapper">
          <as-split direction="horizontal">
            <as-split-area [size]="!showNlpOrMetadata ? 100 : 50" [minSize]="25">
              <div
                class="content"
                [class.content--full-width]="
                  !showNlpOrMetadata && !showPii && !showOriginalLanguage && !showSmartSummary
                "
              >
                <div class="content-header">
                  <div class="search-container">
                    <xi-search-box
                      [placeholder]="t('search')"
                      [stretch]="true"
                      [value]="searchValue"
                      outline="none"
                      textSize="small"
                      (onCancelClick)="searchDocument('')"
                      (keyup)="searchDocument($event.target.value)"
                    ></xi-search-box>
                  </div>
                  @if (options.backAction) {
                    <button
                      type="button"
                      class="document-view__back-action"
                      (click)="close.emit(false)"
                    >
                      <xi-icon icon="arrow_left"></xi-icon>
                      {{ options.backAction.label }}
                    </button>
                  }
                </div>
                <div class="document-content">
                  <div class="document-description">
                    <ng-container
                      *ngTemplateOutlet="documentDescription; context: { $implicit: sections }"
                    ></ng-container>
                  </div>
                </div>
                @if (document.audioPath) {
                  <div
                    class="audio-player-container"
                    [class.document-view--disabled]="isPlaybackDisabled"
                  >
                    <xi-audio-player
                      [source]="documentAudioSrc"
                      [controls]="true"
                      [audioPlayerProperties]="{ currentTime: audioCurrentTime }"
                    ></xi-audio-player>
                  </div>
                }
              </div>
            </as-split-area>
            @if (!options.hideRightSplitArea) {
              <as-split-area
                [size]="!showScoringMetrics && !showNlpOrMetadata && !showSmartSummary ? 0 : 50"
                [minSize]="25"
              >
                <div
                  class="aside"
                  [class.aside--full-width]="
                    !showNlpOrMetadata &&
                    !showPii &&
                    !showOriginalLanguage &&
                    !showSmartSummary &&
                    !showScoringMetrics
                  "
                >
                  @if (showNlpOrMetadata) {
                    @if (editorState !== 'sentiment') {
                      <div class="scrollbar-content">
                        @if (editorState === 'none') {
                          <div #tagScroll @editorTrigger>
                            @if (canShowNlpData) {
                              <app-document-nlp-output
                                [options]="options"
                                [document]="document"
                                [sectionFilter]="sectionFilter"
                                [(closedGroups)]="closedGroups"
                                (entityToEditChange)="onEntityToEditChange($event)"
                                (topicToEditChange)="onTopicToEditChange($event)"
                                (sentimentToEdit)="onEditSentiment($event)"
                                (documentSectionsChange)="onDocumentSectionsChange($event)"
                                (clearSearchValue)="onClearSearchValue()"
                                (removeSectionFilter)="onSectionFilterRemoved()"
                                (highlightChange)="highlightChange.emit($event)"
                              ></app-document-nlp-output>
                            }
                            @if (isMetadataVisible) {
                              <div class="group-name">
                                <div class="chevron" (click)="onToggleMetadata()">
                                  @if (isMetadataExpanded) {
                                    <img src="assets/icons/up-line-gray-24-px.svg" alt="" />
                                  }
                                  @if (!isMetadataExpanded) {
                                    <img src="assets/icons/down-line-gray-24-px.svg" alt="" />
                                  }
                                </div>
                                <div class="text">
                                  {{
                                    t(
                                      sectionFilter ? 'section-metadata-header' : 'metadata-header',
                                      {
                                        count: customFields.length,
                                      }
                                    )
                                  }}
                                </div>
                              </div>
                              <div class="meta-list" [ngClass]="{ hidden: !isMetadataExpanded }">
                                @for (field of customFields; track field) {
                                  <div class="meta-item custom-field">
                                    <span class="label" [matTooltip]="field.longLabel"
                                      >{{ field.longLabel }}:
                                    </span>
                                    @if (field.isAlias) {
                                      <span class="value">{{ field.value }}</span>
                                    } @else {
                                      @switch (field.type) {
                                        @case ('number') {
                                          <span class="value">
                                            {{ field.value | number: field.pipeParams }}
                                          </span>
                                        }
                                        @case ('date') {
                                          <span class="value">
                                            {{ field.value | lexDate }}
                                          </span>
                                        }
                                        @case ('url') {
                                          <a
                                            class="value"
                                            href="{{ document.url }}"
                                            target="_blank"
                                            >{{ field.value }}</a
                                          >
                                        }
                                        @default {
                                          <span class="value">{{ field.value }}</span>
                                        }
                                      }
                                    }
                                  </div>
                                }
                                @if (isTagsDisplaying && !sectionFilter) {
                                  <div class="meta-item custom-field tags-field">
                                    <span class="label">{{ t('tags') }}:</span>
                                    @if (!isReadOnly && !options.isDashboardLocked) {
                                      <div class="add-tag-button">
                                        <xi-icon-button
                                          color="orca"
                                          icon="fab_plus"
                                          (onClick)="addTagsButtonClicked()"
                                        >
                                        </xi-icon-button>
                                      </div>
                                    }
                                  </div>
                                  @if (document.tags.length || showTagInput) {
                                    <mat-form-field
                                      appearance="outline"
                                      color="white"
                                      class="tags-form"
                                    >
                                      <mat-chip-grid #tagChipList [aria-label]="t('document-tags')">
                                        @for (tag of document.tags; track tag) {
                                          <app-lex-xi-tag
                                            [canUserDelete]="
                                              !isReadOnly && !options.isDashboardLocked
                                            "
                                            [text]="tag"
                                            type="user"
                                            (onClick)="showDeleteTagDialog(tag)"
                                          ></app-lex-xi-tag>
                                        }
                                        <input
                                          [disabled]="!showTagInput"
                                          #tagInput
                                          #trigger="matAutocompleteTrigger"
                                          (blur)="closeAutocompletePanel($event, trigger)"
                                          (keydown)="(!isTagCompletionInProgress)"
                                          [formControl]="tagFormControl"
                                          [matAutocomplete]="tagAutocomplete"
                                          [matChipInputFor]="tagChipList"
                                          [matChipInputSeparatorKeyCodes]="tagSeparatorKeysCodes"
                                          (matChipInputTokenEnd)="addTagEvent($event)"
                                          (paste)="onTextPasted($event)"
                                        />
                                      </mat-chip-grid>
                                      <mat-autocomplete
                                        #tagAutocomplete="matAutocomplete"
                                        (optionSelected)="autocompleteOptionSelected($event)"
                                      >
                                        @for (tag of autocompleteTags | async; track tag) {
                                          <mat-option [value]="tag">
                                            {{ tag }}
                                          </mat-option>
                                        }
                                      </mat-autocomplete>
                                    </mat-form-field>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                        @if (editorState === 'entity') {
                          <div @editorTrigger>
                            <app-entity-editor
                              [options]="options"
                              [projectId]="options.projectId"
                              [sentimentScale]="options.sentimentScale"
                              [entityToEdit]="entityToEdit"
                              [configId]="analysis.configId"
                              [analysisConfigId]="analysis.analysisConfigId"
                              [isLoading]="isLoading.editEntity"
                              (cancel)="cancelEntity()"
                              (delete)="deleteEntity($event)"
                              (save)="saveEntity($event)"
                            ></app-entity-editor>
                          </div>
                        }
                        @if (editorState === 'topic') {
                          <div @editorTrigger>
                            <app-topic-editor
                              [document]="document"
                              [options]="options"
                              [topicToEdit]="topicToEdit"
                              [configId]="analysis.configId"
                              [analysisConfigId]="analysis.analysisConfigId"
                              [isLoading]="isLoading.editTopic"
                              (cancel)="cancelTopic()"
                              (delete)="deleteTopic($event)"
                              (save)="saveTopic($event)"
                            ></app-topic-editor>
                          </div>
                        }
                      </div>
                    }
                  } @else if (showScoringMetrics) {
                    <div class="scrollbar-content">
                      <app-document-scoring-metrics
                        [sections]="sections"
                        [sectionFilter]="sectionFilter"
                        [vectorMetrics]="document.vectorMetrics"
                        [scalarMetrics]="document.scalarMetrics"
                        [metadata]="customFields"
                        [settings]="documentScoringMetricsSettings"
                        (selectSection)="onSectionToggled($event.section, $event.index)"
                        (settingsChanged)="onScoringMetricsSettingsChanged($event)"
                      ></app-document-scoring-metrics>
                    </div>
                  } @else {
                    <div class="content">
                      @if (!showSmartSummary) {
                        <div class="search-container">
                          <xi-search-box
                            [placeholder]="t('search-original')"
                            [hasOutline]="true"
                            [stretch]="true"
                            textSize="small"
                            (onCancelClick)="searchDocument('', true)"
                            (keyup)="searchDocument($event.target.value, true)"
                          ></xi-search-box>
                        </div>
                      }
                      <div class="document-content">
                        <div class="document-description">
                          <ng-container
                            *ngTemplateOutlet="
                              showSmartSummary ? documentSummary : documentDescription;
                              context: { $implicit: originalSections }
                            "
                          ></ng-container>
                        </div>
                      </div>
                    </div>
                  }
                  @if (editorState === 'sentiment') {
                    <div @editorTrigger class="scrollbar-content">
                      <app-sentiment-editor
                        [projectId]="options.projectId"
                        [sentimentScale]="options.sentimentScale"
                        [sentimentScore]="analysis.sentimentScore"
                        [sentimentValue]="analysis['sentimentPolarity' + options.sentimentScale]"
                        [isLoading]="isLoading.editSentiment"
                        (cancel)="cancelSentiment()"
                        (save)="saveSentiment($event)"
                      ></app-sentiment-editor>
                    </div>
                  }
                </div>
              </as-split-area>
            }
          </as-split>
        </div>
        @if (!options.hideRightSplitArea) {
          <div class="content-actions">
            @if (viewType === 'conversational') {
              <div>
                <xi-icon-button
                  icon="analytics"
                  buttonSize="large"
                  [fill]="showScoringMetrics ? 'fill' : 'no-fill'"
                  [tooltip]="{ text: t('show-scoring') }"
                  (onClick)="onScoringMetricsToggle()"
                ></xi-icon-button>
              </div>
            }
            @if (canShowNlpData || canShowMetadata) {
              <div>
                <xi-icon-button
                  icon="feedback"
                  buttonSize="large"
                  [fill]="showNlpOrMetadata ? 'fill' : 'no-fill'"
                  [tooltip]="{ text: t('show-nlp') }"
                  (onClick)="onNlpToggle()"
                ></xi-icon-button>
              </div>
            }
            @if (
              analysis.redactedText &&
              analysis.redactedText !== analysis.translatedText &&
              analysis.redactedText !== document.text
            ) {
              <div [matTooltip]="isAlwaysRedact ? t('no-unredact-permission') : t('show-pii')">
                <xi-icon-button
                  icon="folder_private"
                  buttonSize="large"
                  [disabled]="editorState === 'sentiment' || isAlwaysRedact"
                  [fill]="piiToggle ? 'fill' : 'no-fill'"
                  (onClick)="onPiiToggle()"
                ></xi-icon-button>
              </div>
            }
            @if (analysis.translatedText) {
              <div
                [matTooltip]="
                  analysis.redactedText && isAlwaysRedact
                    ? t('no-unredact-permission')
                    : t('show-original-language')
                "
              >
                <xi-icon-button
                  icon="translation"
                  buttonSize="large"
                  [disabled]="
                    editorState === 'sentiment' || (analysis.redactedText && isAlwaysRedact)
                  "
                  [fill]="originalLanguageToggle ? 'fill' : 'no-fill'"
                  (onClick)="onOriginalLanugageToggle()"
                ></xi-icon-button>
              </div>
            }
            @if (hasLLMFeature) {
              <div [matTooltip]="smartSummaryTooltip">
                <xi-icon-button
                  icon="app_announcements"
                  buttonSize="large"
                  [disabled]="isSmartSummaryDisabled"
                  [fill]="smartSummaryToggle ? 'fill' : 'no-fill'"
                  (onClick)="onSmartSummaryToggle()"
                ></xi-icon-button>
              </div>
            }
          </div>
        }
      </div>
      <div
        class="document-view__actions"
        [class.document-view__actions--full-width]="
          !showNlpOrMetadata &&
          !showPii &&
          !showOriginalLanguage &&
          !showSmartSummary &&
          !showScoringMetrics
        "
      >
        <div class="document-view__pagination">
          @if (page.count > 1) {
            <div class="document-view__pagination-stepper">
              <div
                class="double-arrow"
                (click)="prevDocument()"
                [ngClass]="{ disabled: computedIndex === 0 || editorState !== 'none' }"
              >
                <img src="assets/icons/up-doubleline-blue-24-px.svg" alt="" />
              </div>
              <div
                class="numbers"
                [innerHtml]="t('page-of-total', { page: computedIndex + 1, total: page.count })"
              ></div>
              <div
                class="double-arrow"
                (click)="nextDocument()"
                [ngClass]="{ disabled: !canMoveNextDocument }"
                [matTooltip]="!indexBelowMaxLimit ? t('document-too-high') : ''"
              >
                <img src="assets/icons/down-doubleline-blue-24-px.svg" alt="" />
              </div>
            </div>
            <div class="document-view__pagination-go">
              <span>{{ t('go-to-document') }}</span>
              <xi-input-field
                type="number"
                [min]="1"
                [max]="page.count"
                class="document-view__pagination-doc-number"
                (onChange)="goToDoc = $event.detail.target.value"
                (keydown.enter)="onGoToDocument()"
                appOnlyDigit
              ></xi-input-field>
              <xi-default-button
                [disabled]="
                  !goToDoc || goToDoc > ES_MAX_DOCUMENT_SEARCH_LIMIT || this.editorState !== 'none'
                "
                [text]="t('go')"
                (onClick)="onGoToDocument()"
                [matTooltip]="goToDoc > ES_MAX_DOCUMENT_SEARCH_LIMIT ? t('document-too-high') : ''"
              ></xi-default-button>
            </div>
          }
        </div>
        @if (!isReadOnly && !options.isDashboardLocked) {
          <div>
            <xi-default-button
              [text]="t('delete-document')"
              color="warning"
              (onClick)="showDeleteDocumentDialog()"
            ></xi-default-button>
          </div>
        }
      </div>
    </div>
  }

  <ng-template #documentDescription let-sections>
    @if (document?.title) {
      <div class="document-title">
        <ng-container *appMiddleEllipsize="document.title"></ng-container>
      </div>
    }
    @for (section of sections; track section.sectionId; let i = $index; let last = $last) {
      @switch (viewType) {
        @case ('conversational') {
          @if (section.type === 'summary' || section.type === 'other') {
            <app-summary-section
              [section]="section"
              [sectionFilter]="sectionFilter"
              [isPlaybackDisabled]="isPlaybackDisabled"
              [isLastSection]="last"
              (toggleSection)="onSectionToggled($event, i)"
              (toggleSectionLabel)="onSectionLabelToggled($event)"
              (toggleAudioPlayer)="audioCurrentTime = $event.playbackOffset"
            ></app-summary-section>
          } @else {
            <app-conversational-section
              [section]="section"
              [sectionFilter]="sectionFilter"
              [isPlaybackDisabled]="isPlaybackDisabled"
              [colorIndex]="speakerColorIndices[section.label] ?? 0"
              (toggleSection)="onSectionToggled($event, i)"
              (toggleSectionLabel)="onSectionLabelToggled($event, i)"
              (toggleAudioPlayer)="audioCurrentTime = $event.playbackOffset"
            ></app-conversational-section>
          }
        }
        @case ('default') {
          <app-default-section
            [section]="section"
            [sectionFilter]="sectionFilter"
            [isPlaybackDisabled]="isPlaybackDisabled"
            (toggleSection)="onSectionToggled($event, i)"
            (toggleSectionLabel)="onSectionLabelToggled($event, i)"
            (toggleAudioPlayer)="audioCurrentTime = $event.playbackOffset"
          ></app-default-section>
        }
      }
    }
  </ng-template>

  <ng-template #documentSummary let-sections>
    @if (!sections.length) {
      <div class="summary-error">
        {{ lastSmartSummaryError }}
      </div>
    }
    @for (section of sections; track section; let i = $index) {
      <div markdown [data]="section.value" class="summary-section"></div>
    }
  </ng-template>
</ng-container>
